import { action, computed, makeObservable, observable, runInAction, reaction } from 'mobx';

import { eventBus } from 'mobx-event-bus2';

import DataMixin from 'utils/stores/DataMixin';

import Logger from 'utils/logger';

import { NotificationSettings, NotificationSettingsUpdateData } from 'utils/api/modules/user/user.types';

import RootStore from 'stores/Root';
import API from 'utils/api';
import { NotificationTarget } from 'core/constants';

class NotificationSettingsStore extends DataMixin {
  @observable isLoading = false;

  @observable notificationsSettings?: NotificationSettings[];

  @observable isUpdating = false;

  @observable showNewUpdatesSubOptions = false;

  private isNotificationTargetActiveForEmail(target: NotificationTarget): boolean {
    const notificationsSetting = this.notificationsSettings?.find((settings) => settings.target === target);
    if (notificationsSetting) {
      if (notificationsSetting.isActive && notificationsSetting.email) {
        return true;
      }
      return false;
    }
    return false;
  }

  @computed get isNewAssignmentsEmailSettingsEnabled(): boolean {
    return (
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_TASK_ASSIGNED_TO_ME) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_INCIDENT_ASSIGNED_TO_ME) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_EVENT_ASSIGNED_TO_ME)
    );
  }

  @computed get isMentionsEmailSettingEnabled(): boolean {
    return (
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_TASK_MESSAGE) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_INCIDENT_MESSAGE) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_INCIDENT_SUBMISSION_MESSAGE) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_EVENT_MESSAGE) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_INCIDENT_SUBMISSION_MESSAGE)
    );
  }

  @computed get isNewIncidentsEmailSettingEnabled(): boolean {
    return this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_INCIDENT_REPORTED);
  }

  @computed get isNewAuditsEmailSettingEnabled(): boolean {
    return this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_FORM_SUBMITTED);
  }

  @computed get isNewUpdatesSettingsEnabled(): boolean {
    return (
      this.isNotificationTargetActiveForEmail(NotificationTarget.EVENT_COLLABORATED) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.EVENT_COMMENT) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.EVENT_STAGE_CHANGED)
    );
  }

  @computed get isEventCollaboratedEmailSettingEnabled(): boolean {
    return this.isNotificationTargetActiveForEmail(NotificationTarget.EVENT_COLLABORATED);
  }

  @computed get isEventCommentEmailSettingEnabled(): boolean {
    return this.isNotificationTargetActiveForEmail(NotificationTarget.EVENT_COMMENT);
  }

  @computed get isEventStageChangedEmailSettingEnabled(): boolean {
    return this.isNotificationTargetActiveForEmail(NotificationTarget.EVENT_STAGE_CHANGED);
  }

  @computed get isNewEventsReportedEmailSettingEnabled(): boolean {
    return this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_EVENT_REPORTED);
  }

  @computed get isActionUpdatesEmailSettingEnabled(): boolean {
    return (
      this.isNotificationTargetActiveForEmail(NotificationTarget.STATUS_UPDATE_OF_MY_TASK) ||
      this.isNotificationTargetActiveForEmail(NotificationTarget.NEW_COMMENT_IN_MY_TASK)
    );
  }

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);

    makeObservable(this);

    eventBus.register(this);

    // Automatically show sub-options when any of them are enabled
    reaction(
      () => this.isNewUpdatesSettingsEnabled,
      (isEnabled) => {
        if (isEnabled && !this.showNewUpdatesSubOptions) {
          this.showNewUpdatesSubOptions = true;
        }
      }
    );
  }

  @action.bound
  async loadData(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    try {
      this.isLoading = true;
      const response = await this.api.user.loadNotificationSettings()();
      const result = response.data;
      runInAction(() => {
        this.notificationsSettings = result;
        this.isFailure = false;
        this.isLoaded = true;
      });
    } catch (e) {
      runInAction(() => {
        this.isFailure = true;
        this.isLoaded = false;
      });
      Logger.error('Invalid form list API response', e);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async updatedNotificationSettings(
    notificationTarget: NotificationTarget,
    data: Partial<NotificationSettings>,
    targetsLength: number,
    index: number
  ): Promise<void> {
    try {
      const { data: result } = await this.api.user.updateNotificationSetting(notificationTarget, data)();
      if (this.notificationsSettings) {
        const resultIndexInSettings = this.notificationsSettings.findIndex(
          (settings) => settings.target === result.target
        );
        if (resultIndexInSettings > -1) {
          const updatedNotificationSettings = this.notificationsSettings;
          updatedNotificationSettings[resultIndexInSettings] = result;
          runInAction(() => {
            this.notificationsSettings = updatedNotificationSettings;
          });
        } else {
          runInAction(() => {
            this.notificationsSettings = [...(this.notificationsSettings || []), result];
          });
        }
      } else {
        runInAction(() => {
          this.notificationsSettings = [result];
        });
      }
      targetsLength - 1 === index &&
        this.store.notification.enqueueSuccessSnackbar('Notification settings successfully updated');
    } catch (ex) {
      Logger.error('Invalid update notification settings API response.', ex);
      targetsLength - 1 === index &&
        this.store.notification.enqueueErrorSnackbar('Notification settings could not be updated');
    }
  }

  async updateSettingsHelper(
    targets: NotificationTarget[],
    updatedSettings: NotificationSettingsUpdateData
  ): Promise<void> {
    try {
      this.isUpdating = true;
      if (Object.keys(updatedSettings).length === 0) {
        throw new Error('Cannot update settings with empty object');
      }
      targets.forEach(async (target, index) => {
        await this.updatedNotificationSettings(target, updatedSettings, targets.length, index);
      });
    } catch (e) {
      // @ts-ignore
      Logger.error(e);
      this.store.notification.enqueueErrorSnackbar('Unable to update notification settings. Please try again shortly.');
    } finally {
      this.isUpdating = false;
    }
  }

  @action.bound
  async updateNewAssignmentsSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper(
      [
        NotificationTarget.NEW_TASK_ASSIGNED_TO_ME,
        NotificationTarget.NEW_INCIDENT_ASSIGNED_TO_ME,
        NotificationTarget.NEW_EVENT_ASSIGNED_TO_ME,
        NotificationTarget.NEW_SITE_ASSIGNED_TO_ME,
      ],
      updatedSettings
    );
  }

  @action.bound
  async updateMentionsSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper(
      [
        NotificationTarget.NEW_TASK_MESSAGE,
        NotificationTarget.NEW_INCIDENT_MESSAGE,
        NotificationTarget.NEW_INCIDENT_SUBMISSION_MESSAGE,
        NotificationTarget.NEW_EVENT_MESSAGE,
        NotificationTarget.NEW_ASSET_MESSAGE,
        NotificationTarget.NEW_SITE_MESSAGE,
      ],
      updatedSettings
    );
  }

  @action.bound
  async updateNewIncidentsSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper([NotificationTarget.NEW_INCIDENT_REPORTED], updatedSettings);
  }

  @action.bound
  async updateNewAuditsSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper([NotificationTarget.NEW_FORM_SUBMITTED], updatedSettings);
  }

  @action.bound
  async updateNewUpdatesSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper(
      [NotificationTarget.EVENT_COMMENT, NotificationTarget.EVENT_COLLABORATED, NotificationTarget.EVENT_STAGE_CHANGED],
      updatedSettings
    );
  }

  @action.bound
  async updateEventCollaboratedSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper([NotificationTarget.EVENT_COLLABORATED], updatedSettings);
  }

  @action.bound
  async updateEventCommentSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper([NotificationTarget.EVENT_COMMENT], updatedSettings);
  }

  @action.bound
  async updateEventStageChangedSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper([NotificationTarget.EVENT_STAGE_CHANGED], updatedSettings);
  }

  @action.bound
  async updateNewEventsReportedSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper([NotificationTarget.NEW_EVENT_REPORTED], updatedSettings);
  }

  @action.bound
  async updateActionUpdatesSettings(updatedSettings: NotificationSettingsUpdateData): Promise<void> {
    await this.updateSettingsHelper(
      [NotificationTarget.STATUS_UPDATE_OF_MY_TASK, NotificationTarget.NEW_COMMENT_IN_MY_TASK],
      updatedSettings
    );
  }

  @action.bound
  setShowNewUpdatesSubOptions(show: boolean): void {
    this.showNewUpdatesSubOptions = show;
  }
}

export default NotificationSettingsStore;
